/* eslint-disable no-restricted-imports */
import React, {
	forwardRef,
	PropsWithChildren,
	Ref,
	useEffect,
	useRef,
	useState,
	useCallback,
} from 'react';
import { Box, BoxProps } from '@mui/material';
import { scrollContainerSX } from '@/components/blocks/ScrollContainer/style/container';
import { combineSX } from '@/utils/combineSX';
import { DpContainer } from '@/components/blocks/DpContainer';

interface Props extends BoxProps {
	framesx?: BoxProps['sx'];
	prevElement?: JSX.Element | null;
	nextElement?: JSX.Element | null;
	mobileNoPaddingRight?: boolean;
}

export const DpCenterScrollContainer = forwardRef(
	(
		{
			children,
			sx,
			prevElement,
			nextElement,
			id,
			framesx,
			mobileNoPaddingRight,
			...props
		}: PropsWithChildren<Props>,
		ref: Ref<HTMLDivElement>
	) => {
		const [hasScroll, setHasScroll] = useState(false);
		const internalRef = useRef<HTMLDivElement | null>(null);

		const setRefs = useCallback(
			(node: HTMLDivElement | null) => {
				internalRef.current = node;
				if (typeof ref === 'function') {
					ref(node);
				} else if (ref) {
					(ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
				}
			},
			[ref]
		);

		useEffect(() => {
			const checkScroll = () => {
				if (internalRef.current) {
					const { scrollWidth, clientWidth } = internalRef.current;
					// Add a small buffer (e.g., 2px) to prevent floating point issues and ensure the scroll is real
					setHasScroll(scrollWidth > clientWidth + 2);
				}
			};

			checkScroll();

			const observer = new ResizeObserver(() => checkScroll());
			if (internalRef.current) {
				observer.observe(internalRef.current);
			}
			window.addEventListener('resize', checkScroll);

			return () => {
				observer.disconnect();
				window.removeEventListener('resize', checkScroll);
			};
		}, [children, sx]);

		return (
			<DpContainer
				framesx={framesx}
				sx={{ position: 'relative' }}
				id={id}
				mobileNoPaddingRight={mobileNoPaddingRight}
				withoutmargin
			>
				{hasScroll && prevElement}
				<Box
					ref={setRefs}
					sx={combineSX([
						scrollContainerSX,
						{
							display: 'flex',
							flexDirection: 'row',
							width: '100%',
							gap: '20px',
						},
						...(Array.isArray(sx) ? sx : [sx]),
					])}
					{...props}
				>
					{children}
				</Box>
				{hasScroll && nextElement}
			</DpContainer>
		);
	}
);
